import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BarChart3, Download, RefreshCw, Calendar, TrendingUp,
  FileText, CheckCircle, XCircle, Clock, Filter
} from 'lucide-react';

const API = `${window.location.origin}/api`;

const EInvoiceReports = () => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [selectedProperty, year, month]);

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${API}/einvoice/properties`);
      setProperties(res.data.properties || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', year);
      params.append('month', month);
      if (selectedProperty !== 'all') {
        params.append('property_id', selectedProperty);
      }
      
      const res = await axios.get(`${API}/einvoice/reports/monthly?${params.toString()}`);
      setReport(res.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('format', 'csv');
      
      // Get date range for current month
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      params.append('date_from', startDate.toISOString().split('T')[0]);
      params.append('date_to', endDate.toISOString().split('T')[0]);
      
      if (selectedProperty !== 'all') {
        params.append('property_id', selectedProperty);
      }
      
      const response = await axios.get(`${API}/einvoice/reports/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `einvoices_${year}_${month}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount || 0);
  };

  const getStatusIcon = (status) => {
    const icons = {
      validated: <CheckCircle className="w-4 h-4 text-green-500" />,
      rejected: <XCircle className="w-4 h-4 text-red-500" />,
      pending: <Clock className="w-4 h-4 text-yellow-500" />,
      submitted: <FileText className="w-4 h-4 text-blue-500" />
    };
    return icons[status] || icons.pending;
  };

  const getStatusColor = (status) => {
    const colors = {
      validated: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      pending: 'bg-yellow-100 text-yellow-700',
      submitted: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return colors[status] || colors.pending;
  };

  const statusBreakdown = report?.by_status || {};

  return (
    <div className="space-y-6" data-testid="einvoice-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">e-Invoice Reports</h1>
          <p className="text-slate-600">Analytics and export for e-Invoices</p>
        </div>
        <Button onClick={handleExportCSV} disabled={exporting}>
          {exporting ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-xs text-slate-500">Property</Label>
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Properties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-slate-500">Month</Label>
              <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-slate-500">Year</Label>
              <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button variant="outline" onClick={fetchReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Invoices</p>
                    <p className="text-3xl font-bold">{report?.total_invoices || 0}</p>
                  </div>
                  <FileText className="w-10 h-10 text-blue-500 opacity-30" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Subtotal</p>
                    <p className="text-2xl font-bold">{formatCurrency(report?.total_amount)}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-500 opacity-30" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Tax (SST)</p>
                    <p className="text-2xl font-bold">{formatCurrency(report?.total_tax)}</p>
                  </div>
                  <BarChart3 className="w-10 h-10 text-purple-500 opacity-30" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Grand Total</p>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(report?.total_with_tax)}</p>
                  </div>
                  <Calendar className="w-10 h-10 text-blue-500 opacity-30" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>LHDN Status Breakdown</CardTitle>
              <CardDescription>Invoice submission status for {months.find(m => m.value === month)?.label} {year}</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(statusBreakdown).length === 0 ? (
                <p className="text-center py-8 text-slate-500">No invoices for this period</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(statusBreakdown).map(([status, data]) => (
                    <div key={status} className={`p-4 rounded-lg ${getStatusColor(status)}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(status)}
                        <span className="font-medium capitalize">{status}</span>
                      </div>
                      <p className="text-2xl font-bold">{data.count}</p>
                      <p className="text-sm opacity-75">{formatCurrency(data.amount)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Validation Rate */}
          {report?.total_invoices > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Validation Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ 
                          width: `${((statusBreakdown.validated?.count || 0) / report.total_invoices) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">
                      {Math.round(((statusBreakdown.validated?.count || 0) / report.total_invoices) * 100)}%
                    </p>
                    <p className="text-sm text-slate-500">
                      {statusBreakdown.validated?.count || 0} of {report.total_invoices} validated
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Invoices Table */}
          {report?.invoices?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Invoice Details</CardTitle>
                <CardDescription>All invoices for this period</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Guest</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.invoices.slice(0, 20).map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                        <TableCell>{inv.guest_name || '-'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.tax_amount)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(inv.total)}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(inv.lhdn_status)}>
                            {inv.lhdn_status?.toUpperCase()}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {report.invoices.length > 20 && (
                  <p className="text-center py-4 text-sm text-slate-500">
                    Showing 20 of {report.invoices.length} invoices. Export to view all.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default EInvoiceReports;
